import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useState, useCallback } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
  Switch,
  Button,
  Chip,
  CircularProgress
} from "@mui/material";

import { get } from "lodash";
import '../css/font-awesome-pro.css';


import prediction1 from "../images/temp-01.png";
import prediction2 from "../images/temp-02.png";
import prediction3 from "../images/temp-03.png";
import prediction4 from "../images/temp-04.png";

import { getData, getSerialData, switchDevice } from "../api";

/* ================= CONSTANTS ================= */

const HEALTH_LEVELS = [
  { label: "Excellent", icon: prediction1, color: "#10b981", progress: 100 },
  { label: "Good", icon: prediction2, color: "#3b82f6", progress: 75 },
  { label: "Fair", icon: prediction3, color: "#f59e0b", progress: 50 },
  { label: "Poor", icon: prediction4, color: "#ef4444", progress: 25 },
  { label: "Very Poor", icon: prediction4, color: "#991b1b", progress: 10 }
];

const getHealthIndex = (runtime, motorHealth) => {
  const step = motorHealth / 5;
  if (runtime < step) return 0;
  if (runtime < step * 2) return 1;
  if (runtime < step * 3) return 2;
  if (runtime < step * 4) return 3;
  return 4;
};

/* ================= MAIN ================= */

export default function DeviceData() {
  const navigate = useNavigate();
  const { serialNumber } = useParams();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);

  /* ---------- Derived State ---------- */

  const runtimeHours = useMemo(
    () => get(data, "data.runtime", 0) / 60,
    [data]
  );

  const motorHealth = useMemo(
    () => get(data, "motorHealth", 500),
    [data]
  );

  const healthIndex = useMemo(
    () => getHealthIndex(runtimeHours, motorHealth),
    [runtimeHours, motorHealth]
  );

  const healthData = HEALTH_LEVELS[healthIndex];
  const isActive = Boolean(get(data, "isActive"));
  const deviceStatus = Boolean(get(data, "data.status"));

  const detailsUrl = serialNumber
    ? `#home/details/${serialNumber}`
    : "#home/details/";

  /* ---------- Data Fetching ---------- */

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const resp = serialNumber
        ? await getSerialData(serialNumber)
        : await getData();
      setData(resp || {});
    } catch (error) {
      console.error("Error fetching device data:", error);
    } finally {
      setLoading(false);
    }
  }, [serialNumber]);

  useEffect(() => {
    const token = sessionStorage.getItem("token");
    const isAdmin = sessionStorage.getItem("isAdmin") === "true";

    if (!token) return navigate("/", { replace: true });
    if (isAdmin && !serialNumber) return navigate("list", { replace: true });

    fetchData();
  }, [navigate, serialNumber, fetchData]);

  /* ---------- Event Handlers ---------- */

  const handleSwitchChange = async (e) => {
    try {
      setLoading(true);
      await switchDevice(data.machSerial, e.target.checked);
      setTimeout(fetchData, 1500);
    } catch (error) {
      console.error("Error switching device:", error);
      setLoading(false);
    }
  };

  /* ================= RENDER ================= */

  return (
    <Box
      sx={{
        p: { xs: 2, md: 1 },
        maxHeight: "100vh"
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: "auto" }}>
        {/* Header Section */}
      
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <CircularProgress />
          </Box>
        )}

        <Grid container spacing={3} >
            {/* ================= Device Identity Card ================= */}
            <Grid item xs={12} lg={6}>
              <Card
                sx={{
                  height: "100%",
                  borderRadius: 3,
                  color: "black"
                }}
              >
                <CardContent sx={{ p: 1.5 }}>
                  <Stack spacing={1}>
                    <Typography
                      variant="overline"
                      sx={{ opacity: 0.9, fontWeight: 600, letterSpacing: 1 }}
                    >
                      Device Identity
                    </Typography>

                    <Box>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        Device ID
                      </Typography>

                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography variant="h6" fontWeight={700}>
                          {get(data, "deviceId", "-")}
                        </Typography>

                        <Chip
                          label={isActive ? "Online" : "Offline"}
                          color={isActive ? "success" : "error"}
                          sx={{
                            fontWeight: 600,
                            px: 1,
                            height: 28
                          }}
                        />
                      </Stack>
                    </Box>


                    <Box>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        Serial Number
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {get(data, "machSerial", "-")}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* ================= Motor Health Card ================= */}
            <Grid item xs={12} sm={6} lg={3}>
              <Card
                sx={{
                  height: "100%",
                  borderRadius: 3,
                  background: "rgba(255,255,255,0.95)"
                }}
              >
                <CardContent sx={{ p: 1.5 }}>
                  <Stack spacing={1.5} alignItems="center" textAlign="center">
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      fontWeight={600}
                      letterSpacing={1}
                    >
                      Motor Health
                    </Typography>

                    <Box
                      sx={{
                        width: 72,
                        height: 72,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}
                    >
                      <img
                        src={healthData.icon}
                        width={40}
                        height={40}
                        alt="health"
                      />
                    </Box>

                    <Typography
                      variant="h6"
                      fontWeight={700}
                      sx={{ color: healthData.color }}
                    >
                      {healthData.label}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* ================= Device Control Card ================= */}
            <Grid item xs={12} sm={6} lg={3}>
              <Card
                sx={{
                  height: "100%",
                  borderRadius: 3,
                  background: "rgba(255,255,255,0.95)"
                }}
              >
                <CardContent
                  sx={{
                    p: 2,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start"
                  }}
                >
                  <Stack spacing={1.5} alignItems="center" textAlign="center">
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      fontWeight={600}
                      letterSpacing={1}
                    >
                      Device Control
                    </Typography>

                    <Typography variant="body1" fontWeight={600}>
                      Immobilize Switch
                    </Typography>

                    <Box
                      sx={{
                        p: 1.2,
                        borderRadius: 2,
                        bgcolor: deviceStatus ? "#10b98122" : "#ef444422",
                        border: `2px solid ${
                          deviceStatus ? "#10b981" : "#ef4444"
                        }`,
                        width: "100%"
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="center"
                        spacing={1.5}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color={deviceStatus ? "success.main" : "error.main"}
                        >
                          {deviceStatus ? "Active" : "Inactive"}
                        </Typography>

                        <Switch
                          checked={deviceStatus}
                          onChange={handleSwitchChange}
                          disabled={loading}
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: "#10b981"
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                              backgroundColor: "#10b981"
                            }
                          }}
                        />
                      </Stack>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

          {/* Device Feed Metrics */}
          <Grid item xs={12} md={6}  >
            <Card
              sx={{
                height: "100%",
                borderRadius: 3,
                background: "rgba(255,255,255,0.95)"
              }}
            >
              <CardContent
                sx={{
                  p: 2.5,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column"
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight={700}
                  gutterBottom
                  color="primary.main"
                >
                  Device Feed
                </Typography>

                <Stack spacing={1.5} sx={{ mt: 1 }}>
                  <MetricRow
                    label="Total Key Cut"
                    value={`${get(data, "data.keycount", "-")} nos`}
                    color="#000000"
                    icon={<i className="fa-solid fa-key" />}
                  />

                  <MetricRow
                    label="Energy Consumption"
                    value={`${get(data, "data.energy", "-")} units`}
                    color="#000000"
                    icon={<i className="fa-solid fa-bolt" />}
                  />

                  <MetricRow
                    label="Motor Runtime"
                    value={`${get(data, "data.runtime", "-")} min`}
                    color="#000000"
                    icon={<i className="fa-solid fa-clock" />}
                  />

                   

                   <MetricRow
                    label="Version"
                    value={get(data, "data.ver", "-")}
                    color="#64748b"
                    icon={<i className="fa-solid fa-circle-info" />}
                  />
                </Stack>
              </CardContent>
            </Card>
          </Grid>


          {/* Device Information */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: "100%",
                borderRadius: 3,
                background: "rgba(255,255,255,0.95)"
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  gutterBottom
                  color="primary.main"
                >
                  Information
                </Typography>

                <Stack spacing={1} sx={{ mt: 2 }}>

                  <MetricRow
                    label="Machine Name"
                    value={`${get(data, "owner.machinename", "DUO IQ")}`}
                    color="#8b5cf6"
                    icon={<i className="fa-solid fa-robot" />}
                  />

                  <MetricRow
                    label="Customer"
                    value={`${get(data, "owner.fName", "")} ${get(data, "owner.lName", "")}`}
                    color="#8b5cf6"
                    icon={<i className="fa-solid fa-user" />}
                  />

                  <MetricRow
                    label="City"
                    value={get(data, "owner.city", "-")}
                    color="#ec4899"
                    icon={<i className="fa-solid fa-location-dot" />}
                  />

                  <MetricRow
                    label="Dealer"
                    value={get(data, "dealer.name", "-")}
                    color="#06b6d4"
                    icon={<i className="fa-solid fa-store" />}
                  />

                  <MetricRow
                    label="Machine Type"
                    value={`${get(data, "machinetype", "-")}`}
                    color="#000000"
                    icon={<i className="fa-solid fa-robot" />}
                  />

                 
                </Stack>

              </CardContent>
            </Card>
          </Grid>

          {/* Alert Banner */}
          <Grid item xs={12} md={8} >
            <Card
              sx={{
                borderRadius: 3,
                background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
                border: "2px solid #f59e0b"
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box
                    sx={{
                      bgcolor: "#f59e0b",
                      borderRadius: "50%",
                      p: 1,
                      display: "flex",
                      minWidth: 36,
                      minHeight: 36,
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    <Typography variant="h6">⚠️</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body1" fontWeight={600} color="#92400e">
                      Device operation active for 15 hours without Wi-Fi connection.
                    </Typography>
                    <Typography variant="body2" color="#b45309" sx={{ mt: 0.5 }}>
                      Please ensure the device is connected to maintain optimal performance.
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* CTA Button */}
          {/* <Grid item xs={4}>
            <Box sx={{ textAlign: "center" }}>
              <Button
                href={detailsUrl}
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  px: 6,
                  py: 2,
                  borderRadius: 3,
                  fontWeight: 700,
                  fontSize: "1rem",
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  boxShadow: "0 8px 20px rgba(102, 126, 234, 0.4)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #5568d3 0%, #6941a0 100%)",
                    boxShadow: "0 12px 28px rgba(102, 126, 234, 0.5)"
                  }
                }}
              >
                View Detailed Device Data
              </Button>
            </Box>
          </Grid> */}

            <Grid item xs={12} md={4}>
            <Card
              sx={{
                height: "100%",
                borderRadius: 2,
                display: "flex",
                flexDirection: "column",
                backgroundColor: "transparent",
                 boxShadow: "none"

              }}
            >
              {/* Empty / content area */}
              <Box sx={{ flexGrow: 1 }} />
          
              {/* Footer */}
              <Box
                sx={{
                  p: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  display: "flex",
                  justifyContent: "center",
                  backgroundColor: "#fff",
                  borderRadius: "10px 10px 8px 8px",
                }}
              >
                <Button
                  href={detailsUrl}
                  variant="contained"
                  size="medium"
                  sx={{
                    px: 4,
                    py: 1.2,
                    borderRadius: 3,
                    fontWeight: 700,
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    boxShadow: "0 6px 16px rgba(102,126,234,0.4)",
                    "&:hover": {
                      boxShadow: "0 10px 22px rgba(102,126,234,0.5)",
                      transform: "translateY(-1px)"
                    }
                  }}
                >
                  View Detailed Analytics
                </Button>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

/* ================= REUSABLE COMPONENTS ================= */


function MetricRow({ label, value, color, icon }) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        p: 1,
        borderRadius: 2,
        bgcolor: `${color}08`,
        border: `1px solid ${color}22`,
        transition: "all 0.2s",
        "&:hover": {
          bgcolor: `${color}15`,
          transform: "translateX(4px)"
        }
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            bgcolor: `${color}22`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.5rem",
            fontWeight: 700,
            color: "#000000"
          }}
        >
          {icon}
        </Box>
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          {label}
        </Typography>
      </Stack>
      <Typography variant="body1" fontWeight={700} color="text.primary">
        {value}
      </Typography>
    </Stack>
  );
}